import useSWR from 'swr';
import { usePolyAuth } from './PolyAuthContext';
import { isSvgFile, svgFileToPng } from '../utils/svgToPng';

/**
 * Custom hook to fetch all accounts for the current user
 * @param {string} sessionToken - The session token for authentication
 * @returns {object} SWR response with data, error, isLoading, and mutate
 */
export const useAccounts = () => {
  const { api_client, local_fetcher, accountId } = usePolyAuth();
  const { data, error, mutate, isLoading } = useSWR(
    local_fetcher ? `/accounts/` : null,
    local_fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  // Helper for optimistic updates
  const optimisticUpdate = async (updateFn, apiCall) => {
    const optimisticData = updateFn(data || []);
    await mutate(optimisticData, false);
    
    try {
      await apiCall();
    } catch (error) {
      // If failed, revalidate to get the correct data back
      mutate();
      throw error;
    }
  };

  const createAccount = async (accountData) => {
    try {
      const response = await api_client.post('/accounts/', accountData);
      return response.data;
    } catch (error) {
      const errorData = error.response?.data || { detail: 'Failed to create account' };
      throw new Error(errorData.detail || 'Failed to create account');
    }
  };
  
  const updateAccount = async (accountData) => {
    try {
      const response = await api_client.patch(`/accounts/${accountId}/`, accountData);
      return response.data;
    } catch (error) {
      const errorData = error.response?.data || { detail: 'Failed to update account' };
      throw new Error(errorData.detail || 'Failed to update account');
    }
  };

  const deleteAccount = async () => {
    try {
      await api_client.delete(`/accounts/${accountId}/`);
      await mutate((current) => (current || []).filter((a) => String(a.id) !== String(accountId)), false);
    } catch (error) {
      const errorData = error.response?.data || { detail: 'Failed to delete account' };
      throw new Error(errorData.detail || 'Failed to delete account');
    }
  };

  /**
   * Get a presigned URL for uploading a logo to S3
   * @param {string} logoType - 'dark' or 'light'
   * @param {string} contentType - MIME type (e.g., 'image/png')
   * @returns {Promise<{upload_url: string, cdn_url: string}>}
   */
  const getLogoUploadUrl = async (logoType, contentType) => {
    try {
      const response = await api_client.post(
        `/accounts/${accountId}/logo-upload-url`,
        { logo_type: logoType, content_type: contentType }
      );
      return response.data;
    } catch (error) {
      const errorData = error.response?.data || { detail: 'Failed to get upload URL' };
      throw new Error(errorData.detail || 'Failed to get upload URL');
    }
  };

  /**
   * Upload a logo file to S3 and return the CDN URL
   * @param {File} file - The image file to upload
   * @param {string} logoType - 'dark' or 'light'
   * @returns {Promise<string>} The CDN URL of the uploaded logo
   */
  const uploadLogoToS3 = async (file, logoType) => {
    // SVG is rasterized to PNG client-side so mobile/web only ever render a
    // safe raster; the backend presigned path still only accepts image/png.
    const upload = isSvgFile(file) ? await svgFileToPng(file) : file;

    const { upload_url, cdn_url } = await getLogoUploadUrl(logoType, upload.type);

    await fetch(upload_url, {
      method: 'PUT',
      body: upload,
      headers: {
        'Content-Type': upload.type,
      },
    });

    return cdn_url;
  };

  /**
   * Update account branding (logo, legal name, address).  Sends each
   * field as a typed top-level key -- the backend no longer accepts an
   * ``extra_data`` blob on the request (PR-451 policy: every settable
   * key is typed and role-gated, the ``extra_data`` column is internal
   * storage).  The previous merge-then-replace dance over a snapshotted
   * ``extra_data`` is gone because there's no longer a sibling-wipe
   * risk to compensate for.
   *
   * @param {Object} branding - Branding data
   * @param {string} [branding.legal_entity] - Legal company name
   * @param {string} [branding.address] - Company address
   * @param {string} [branding.logo_dark] - CDN URL for dark mode logo
   * @param {string} [branding.logo_light] - CDN URL for light mode logo
   */
  const updateAccountBranding = async ({ legal_entity, address, logo_dark, logo_light }) => {
    const payload = {};
    if (legal_entity !== undefined) payload.legal_entity = legal_entity;
    if (address !== undefined) payload.address = address;
    if (logo_dark !== undefined) payload.logo_dark = logo_dark;
    if (logo_light !== undefined) payload.logo_light = logo_light;

    return await updateAccount(payload);
  };

  return {
    accounts: data,
    isLoading,
    isError: error,
    mutate,
    optimisticUpdate,
    createAccount,
    updateAccount,
    deleteAccount,
    getLogoUploadUrl,
    uploadLogoToS3,
    updateAccountBranding,
  };
};

